import Grid from '@config/Grid/Grid'
import Tile from '@components/Tile/Tile'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect, useState } from 'react'
import { GridRowParams } from '@mui/x-data-grid'
import { useNavigate } from 'react-router-dom'
import Table from '@/components/Table/Table'
import { useTablesListColumnConfig } from './hooks/useTablesListColumnConfig'
import { TABLE_KEYS } from '@/components/Table/models/Filter.model'
import Button from '@/components/Button/Button'
import styles from './TablesList.module.scss'
import SelectedTableModal from '@/components/Modal/SelectedTableModal/SelectedTableModal'
import { useTablesSocket } from './hooks/useTablesSocket'
import { WebSocketEvent } from '@/services/webSocketEvent'
import { socketService } from '@/services/socket.service'
import useTablesStore from './hooks/useTablesStore'
import useOrderPageStore from '../OrderPage/hooks/useOrderPageStore'
import TablesAPI from './api/TablesAPI/TablesAPI'

type TableDataType = {
  id: string
  number: string
  status: {
    id: string
    label: string
    tooltipEvent: never[]
  }[]
  guests: number
  currentTotal: string | null
}

type WebSocketTableDataType = {
  currentTotal: number
  numberOfGuests: number
  status: string
  tableIds: string[]
}

function TablesList() {
  const tablesSocket = useTablesSocket()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [showAllTables, setShowAllTables] = useState(true)
  const [mappedTableData, setMappedTableData] = useState<TableDataType[]>([])

  const {
    selectedGuestsNumber,
    selectedTableId,
    selectedTableNumber,
    setSelectedGuestsNumber,
    setSelectedTableId,
    setSelectedTableNumber,
    myTables,
    addMyTable,
    removeMyTable
  } = useTablesStore()

  const { setTableIds, resetOrder } = useOrderPageStore()

  const handleEditOrder = useCallback(
    (tableId: string) => {
      const tableRow = mappedTableData.find(t => t.id === tableId)
      if (tableRow) {
        setSelectedTableId(tableId)
        setSelectedTableNumber(Number(tableRow.number))
        setSelectedGuestsNumber(String(tableRow.guests))
        setTableIds([tableId])
      }
      navigate(`/table/${tableId}`)
    },
    [
      navigate,
      mappedTableData,
      setSelectedTableId,
      setSelectedTableNumber,
      setSelectedGuestsNumber,
      setTableIds
    ]
  )

  const handleMarkAsServed = useCallback(
    (tableId: string, currentStatus: string) => {
      const targetStatus = currentStatus === 'serving' ? 'served' : 'free'
      TablesAPI.putTableStatus(tableId, { status: targetStatus }).then(() => {
        socketService.emit(WebSocketEvent.TABLES_UPDATE, { id: tableId })
      })
    },
    []
  )

  const columns = useTablesListColumnConfig({
    onEdit: handleEditOrder,
    onMarkAsServed: handleMarkAsServed
  })
  const columnsModel = ['number', 'status', 'guests', 'currentTotal', 'actions']

  const handleRowClick = (params: GridRowParams) => {
    if (params.row.status[0].label === 'free') {
      setSelectedTableId(params.row.id)
      setSelectedTableNumber(Number(params.row.number))
      setTableIds([params.row.id])
    } else {
      navigate(`/table/${params.row.id}/serve`, {
        state: { tableStatus: params.row.status[0].label }
      })
    }
  }

  const handleConfirmTableSelection = () => {
    resetOrder()
    setTableIds([selectedTableId])
    addMyTable(selectedTableId)
    navigate(`/table/${selectedTableId}`)
  }

  const handleOnSelectedTableModalClose = () => {
    removeMyTable(selectedTableId)
    setSelectedTableId('')
    setSelectedTableNumber(0)
    setSelectedGuestsNumber('')
  }

  useEffect(() => {
    setSelectedTableId('')
    setSelectedTableNumber(0)
    setSelectedGuestsNumber('')
  }, [])

  const toTableDataType = (
    data: WebSocketTableDataType,
    index: number
  ): TableDataType => ({
    id: data.tableIds[0],
    number: String(index + 1),
    status: [
      {
        id: data.tableIds[0],
        label: data.status,
        tooltipEvent: []
      }
    ],
    guests: data.numberOfGuests,
    currentTotal: data.currentTotal
      ? String((data.currentTotal / 100).toFixed(2))
      : null
  })

  const handleGetTablesData = useCallback(
    (data: WebSocketTableDataType[]) => {
      if (Array.isArray(data)) {
        const mappedData = data.map(
          (tableItem: WebSocketTableDataType, index: number) => ({
            id: tableItem.tableIds[0],
            number: String(index + 1),
            status: [
              {
                id: tableItem.tableIds[0],
                label: tableItem.status,
                tooltipEvent: []
              }
            ],
            guests: tableItem.numberOfGuests,
            currentTotal: tableItem.currentTotal
              ? String((tableItem.currentTotal / 100).toFixed(2))
              : null
          })
        )

        // Auto-add serving tables to My Tables, remove free ones
        data.forEach(tableItem => {
          if (tableItem.status === 'serving') {
            addMyTable(tableItem.tableIds[0])
          } else if (tableItem.status === 'free') {
            removeMyTable(tableItem.tableIds[0])
          }
        })

        setMappedTableData(mappedData)
      }
    },
    [addMyTable, removeMyTable]
  )

  const handleUpdateTableData = useCallback(
    (data: WebSocketTableDataType) => {
      // Auto-add serving tables to My Tables, remove free ones
      if (data.status === 'serving') {
        addMyTable(data.tableIds[0])
      } else if (data.status === 'free') {
        removeMyTable(data.tableIds[0])
      }

      setMappedTableData(prevData =>
        prevData.map((tableItem, index) =>
          tableItem.id === data.tableIds[0]
            ? toTableDataType(data, index)
            : tableItem
        )
      )
    },
    [addMyTable, removeMyTable]
  )

  useEffect(() => {
    tablesSocket.on(WebSocketEvent.TABLES_INIT, handleGetTablesData)
    tablesSocket.on(WebSocketEvent.EXCEPTION, error => {
      console.log('ws error: ', error)
    })

    return () => {
      tablesSocket.off(WebSocketEvent.TABLES_INIT, handleGetTablesData)
    }
  }, [handleGetTablesData])

  useEffect(() => {
    tablesSocket.on(WebSocketEvent.TABLES_UPDATE, handleUpdateTableData)
    tablesSocket.on(WebSocketEvent.EXCEPTION, error => {
      console.log('ws error: ', error)
    })

    return () => {
      tablesSocket.off(WebSocketEvent.TABLES_UPDATE, handleUpdateTableData)
    }
  }, [handleUpdateTableData])

  return (
    <Grid>
      <div className="start-1 col-12">
        <Tile color="black" title={t('TABLE.TABLES_LIST.TITLE')}>
          <div className={styles.tablesListWrapper}>
            <div className={styles.tableButtonsContainer}>
              <Button
                label={t('TABLE.TABLES_LIST.ALL_TABLES')}
                variant="contained"
                color="primary"
                onClick={() => setShowAllTables(true)}
                mobile
                disabled={showAllTables}
              />
              <Button
                label={t('TABLE.TABLES_LIST.MY_TABLES')}
                variant="contained"
                color="primary"
                onClick={() => setShowAllTables(false)}
                mobile
                disabled={!showAllTables}
              />
            </div>
            <div className={styles.tablesListContainer}>
              <Table
                columns={columns}
                columnsModel={columnsModel}
                tableKey={TABLE_KEYS.TABLES_LIST}
                initialRows={
                  showAllTables
                    ? mappedTableData || []
                    : mappedTableData.filter(row => myTables.includes(row.id))
                }
                filterModel={{ items: [] }}
                handleOnDeleteItems={() => null}
                handleOnAddItem={() => null}
                hideToolbar
                hideCheckboxSelection
                hideFooter
                disablePagination
                isLoading={false}
                onRowClick={handleRowClick}
                getRowClassName={params =>
                  params.row.status?.[0]?.label === 'free'
                    ? 'free-table-row'
                    : ''
                }
              />
            </div>
          </div>
        </Tile>
        <SelectedTableModal
          isOpen={selectedTableId !== ''}
          onClose={handleOnSelectedTableModalClose}
          onConfirm={handleConfirmTableSelection}
          tableNumber={selectedTableNumber ?? 0}
          guestsNumberValue={selectedGuestsNumber}
          setGuestsNumberValue={setSelectedGuestsNumber}
        />
      </div>
    </Grid>
  )
}

export default TablesList
